import { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  CloudDone as CloudIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
// REMOVED: import { motion } from 'framer-motion'; - keeping only canvas-confetti for celebration
import confetti from 'canvas-confetti';
import { WizardStepProps } from '../types';
import { BrokerProvider } from '../../../../data/brokers';

interface SuccessStepProps extends WizardStepProps {
  broker: BrokerProvider;
}

const SuccessStep: React.FC<SuccessStepProps> = ({
  broker,
  onNext,
}) => {
  const theme = useTheme();

  // Trigger confetti animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: [theme.palette.primary.main, theme.palette.primary.light],
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [theme]);

  const successIcon = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
          border: `3px solid ${theme.palette.primary.main}`,
          mb: 3,
        }}
      >
        <CloudIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: '100%',
        py: 4,
      }}
    >
      {successIcon}

      <Box>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Data Imported Successfully!
        </Typography>
      </Box>

      <Box
        sx={{ width: '100%', maxWidth: 500 }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Your {broker.displayName} transaction history has been imported.
        </Typography>

        <Box
          sx={{
            p: 3,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CheckIcon sx={{ color: theme.palette.success.main }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Import Complete
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your transaction data has been processed and is ready to view.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            What's next?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • View your portfolio performance and analytics<br />
            • Track your investment returns and dividends<br />
            • Monitor your asset allocation and risk
          </Typography>
        </Box>
      </Box>

      <Box>
        <Button
          variant="contained"
          onClick={onNext}
          endIcon={<ArrowForwardIcon />}
          size="large"
          sx={{
            minWidth: 200,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)',
            boxShadow: theme.customShadows.elevated[theme.palette.mode],
            '&:hover': {
              // REMOVED transform for performance: transform: 'translateY(-2px)',
              boxShadow: theme.customShadows.hover[theme.palette.mode],
            },
          }}
        >
          View Portfolio
        </Button>
      </Box>
    </Box>
  );
};

export default SuccessStep;